/**
 * Console Logger Service
 *
 * Intercepts console.log, console.warn, console.error and stores recent entries
 * for inclusion in bug reports. Preserves original console behavior.
 */

export type ConsoleLogLevel = 'log' | 'warn' | 'error' | 'info' | 'debug';

export interface ConsoleLogEntry {
  level: ConsoleLogLevel;
  timestamp: string;
  message: string;
  args: unknown[];
}

const MAX_ENTRIES = 200;

class ConsoleLoggerService {
  private entries: ConsoleLogEntry[] = [];
  private initialized = false;
  private originalConsole: {
    log: typeof console.log;
    warn: typeof console.warn;
    error: typeof console.error;
    info: typeof console.info;
    debug: typeof console.debug;
  } | null = null;

  /**
   * Initialize the console logger by intercepting console methods.
   * Should be called once at app startup, before any other code runs.
   */
  initialize(): void {
    if (this.initialized) return;

    // Store original console methods
    this.originalConsole = {
      log: console.log.bind(console),
      warn: console.warn.bind(console),
      error: console.error.bind(console),
      info: console.info.bind(console),
      debug: console.debug.bind(console),
    };

    // Intercept each console method
    const levels: ConsoleLogLevel[] = ['log', 'warn', 'error', 'info', 'debug'];

    for (const level of levels) {
      const original = this.originalConsole[level];
      console[level] = (...args: unknown[]) => {
        this.captureEntry(level, args);

        // Suppress noisy third-party library logs from the browser console
        // while still capturing them above for bug reports
        if (!ConsoleLoggerService.isSuppressedLog(args)) {
          original(...args);
        }
      };
    }

    this.initialized = true;
  }

  /**
   * Check if a log call matches known noisy third-party library patterns that
   * should be suppressed from the browser console (but still captured for bug
   * reports). Currently suppresses:
   *
   * - NVIDIA streaming library session lifecycle JSON logs
   * - JupyterLab services WebSocket connection logs
   * - AppStream retry lifecycle + Rerun WASM teardown noise
   * - Rerun WASM viewer's verbose Rust DEBUG/INFO/TRACE module logging
   */
  private static isSuppressedLog(args: unknown[]): boolean {
    if (args.length === 0) return false;
    const first = args[0];
    if (typeof first !== 'string') return false;

    // NVIDIA streaming library emits JSON strings with session lifecycle info
    if (first.startsWith('{') && first.includes('"title"') && first.includes('"request"')) {
      return true;
    }

    // JupyterLab services logs WebSocket connection URLs
    if (first.startsWith('Starting WebSocket:')) {
      return true;
    }

    // Repetitive AppStream retry lifecycle logs are captured in streamLogger
    // and do not need to flood the browser console
    if (
      first.startsWith('[AppStream] connecting:') ||
      first.startsWith('[AppStream] connect failed:') ||
      first.startsWith('[AppStream] scheduling retry #') ||
      first.startsWith('[AppStream] stream stopped:') ||
      first.startsWith('[AppStream] onStart with unexpected status:') ||
      first.startsWith('[AppStream] suppressed NVIDIA library internal rejection:')
    ) {
      return true;
    }

    // Known non-actionable Rerun WASM teardown/runtime suppressions
    if (
      first.startsWith('[Rerun] suppressed WASM teardown error:') ||
      first.startsWith('[Rerun] suppressed WASM runtime panic:')
    ) {
      return true;
    }

    // Rerun eframe WASM viewer logs this on every requestAnimationFrame (~60/s)
    // because the web backend doesn't implement RequestUserAttention
    if (first.includes('Unhandled egui viewport command: RequestUserAttention')) {
      return true;
    }

    // Rerun WASM viewer's verbose Rust logging (eframe/egui/re_*/wgpu/naga
    // module DEBUG/INFO/TRACE lines) floods the console on every viewer start.
    // Drop only the low-severity levels here; WARN/ERROR (real failures such as
    // "WebViewer failure") are not matched and still surface.
    if (/^(DEBUG|TRACE|INFO) (eframe|egui|re_|wgpu|naga)\b/.test(first)) {
      return true;
    }

    return false;
  }

  private captureEntry(level: ConsoleLogLevel, args: unknown[]): void {
    // Serialize args to a readable string
    const message = args
      .map((arg) => {
        if (typeof arg === 'string') return arg;
        try {
          return JSON.stringify(arg, null, 2);
        } catch {
          return String(arg);
        }
      })
      .join(' ');

    // Store simplified args for JSON export
    const serializedArgs = args.map((arg) => {
      if (arg === null || arg === undefined) return arg;
      if (typeof arg === 'string' || typeof arg === 'number' || typeof arg === 'boolean') return arg;
      if (arg instanceof Error) {
        return { name: arg.name, message: arg.message, stack: arg.stack };
      }
      try {
        // Try to serialize, but limit depth to avoid circular references
        return JSON.parse(JSON.stringify(arg));
      } catch {
        return `[${typeof arg}]`;
      }
    });

    const entry: ConsoleLogEntry = {
      level,
      timestamp: new Date().toISOString(),
      message,
      args: serializedArgs,
    };

    this.entries.push(entry);

    // Trim to max entries
    if (this.entries.length > MAX_ENTRIES) {
      this.entries = this.entries.slice(-MAX_ENTRIES);
    }
  }

  /**
   * Get all captured log entries
   */
  getEntries(): ConsoleLogEntry[] {
    return [...this.entries];
  }

  /**
   * Get entries filtered by level
   */
  getEntriesByLevel(level: ConsoleLogLevel): ConsoleLogEntry[] {
    return this.entries.filter((entry) => entry.level === level);
  }

  /**
   * Get only error and warning entries
   */
  getErrorsAndWarnings(): ConsoleLogEntry[] {
    return this.entries.filter((entry) => entry.level === 'error' || entry.level === 'warn');
  }

  /**
   * Clear all captured entries
   */
  clear(): void {
    this.entries = [];
  }

  /**
   * Get the count of entries by level
   */
  getCounts(): Record<ConsoleLogLevel, number> {
    const counts: Record<ConsoleLogLevel, number> = {
      log: 0,
      warn: 0,
      error: 0,
      info: 0,
      debug: 0,
    };

    for (const entry of this.entries) {
      counts[entry.level]++;
    }

    return counts;
  }
}

// Singleton instance
export const consoleLogger = new ConsoleLoggerService();
